<script>
// @ts-check

import { getCountries } from '@/api/utils'
import BaseButton from '@/components/base/BaseButton.vue'
import CheckField from '@/components/fields/CheckField.vue'
import BaseError from '@/components/base/BaseError.vue'
import BaseErrors from '@/components/base/BaseErrors.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseLink from '@/components/base/BaseLink.vue'
import BaseSearchableSelect from '@/components/base/BaseSearchableSelect.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'
import BusinessAccountOnboardingFormAdditional from '@/components/business-account/BusinessAccountOnboardingFormAdditional.vue'
import { useVuelidate } from '@/core/composables/useVuelidate'
import { useLanguageStore } from '@/store/language'
import { maxLength, required, requiredIf, sameAs } from '@vuelidate/validators'
import { mapState } from 'pinia'
import Vuex from 'vuex'

export default {
  name: 'BusinessAccountOnboardingForm',
  components: {
    BaseError,
    BaseErrors,
    BaseLink,
    CheckField,
    LoadingSpinner,
    BusinessAccountOnboardingFormAdditional,
    BaseButton,
    BaseInput,
    BaseSearchableSelect,
    BaseSelect
  },
  setup: () => useVuelidate(),
  data: () => ({
    isLoading: true,
    isSubmitting: false,
    hasOnboardingError: false,
    /** @type {Countries} */
    countryOptions: [],
    swan_terms: false,
    terms: false,
    errorFetchingOnboardingData: false,
    form: {
      birth_country: '',
      birth_town: '',
      birth_zip_code: '',
      business_enum: '',
      payment_volume_enum: '',
      business_description: ''
    }
  }),
  computed: {
    ...Vuex.mapGetters('app', ['isOyService']),
    ...Vuex.mapState('swanAccount', [
      'onboardingData',
      'onboardingLink',
      'onboardingIndustryOptions',
      'onboardingPaymentVolumeOptions'
    ]),
    ...Vuex.mapGetters('companyData', [
      'canApplyForBusinessAccount',
      'company',
      'user'
    ]),
    ...mapState(useLanguageStore, ['language']),
    termsNotFound() {
      return (
        !this.onboardingData?.terms_of_service ||
        !this.onboardingData?.partnership_terms
      )
    },
    showBusinessAccountLoading() {
      return this.isLoading || !this.company || !this.user
    }
  },
  async mounted() {
    try {
      await this.getSwanOnboardingData()
      this.countryOptions = await getCountries(this.language)
    } catch (error) {
      console.error(error)
      this.errorFetchingOnboardingData = true
      this.hasOnboardingError = true
    }
    this.isLoading = false
  },
  validations() {
    return {
      form: {
        birth_country: {
          required: requiredIf(this.isOyService)
        },
        birth_town: {
          required: requiredIf(this.isOyService)
        },
        birth_zip_code: {
          required: requiredIf(this.isOyService)
        },
        business_enum: {
          required
        },
        payment_volume_enum: {
          required
        },
        business_description: {
          required,
          maxLength: maxLength(255)
        }
      },
      terms: {
        requiredAcceptTerms: sameAs(true)
      },
      swan_terms: {
        requiredAcceptTerms: sameAs(true)
      }
    }
  },
  methods: {
    ...Vuex.mapActions('swanAccount', [
      'getSwanOnboardingData',
      'submitSwanOnboarding'
    ]),
    ...Vuex.mapMutations('utils', ['hideBusinessAccountInformationDialog']),
    async validateFormAndOpenSwanOnboarding() {
      if (!(await this.validate())) {
        return
      }

      this.isSubmitting = true

      try {
        await this.submitSwanOnboarding(this.form)
        window.open(this.onboardingLink, '_self')
      } catch (error) {
        console.error(error)
        this.hasOnboardingError = true
      }

      this.isSubmitting = false
    }
  }
}
</script>

<template>
  <div class="onboarding-form flex flex-col gap-y-4 text-secondary">
    <template v-if="isOyService">
      <div
        class="flex flex-1 flex-col gap-y-4 md:flex-row md:gap-x-4 md:gap-y-0"
      >
        <BaseSearchableSelect
          v-model="v$.form.birth_country.$model"
          class="w-full md:w-1/3"
          :options="countryOptions"
          :loading="isLoading"
          :errors="errors.birth_country"
          :label="$t('business_account.onboarding_form.birth_country')"
          :placeholder="$t('business_account.onboarding_form.birth_country')"
          label-path="label"
          value-path="code"
        />
        <BaseInput
          v-model="v$.form.birth_town.$model"
          class="w-full md:w-1/3"
          :errors="errors.birth_town"
          :label="$t('business_account.onboarding_form.birth_city')"
        />
        <BaseInput
          v-model="v$.form.birth_zip_code.$model"
          class="w-full md:w-1/3"
          :errors="errors.birth_zip_code"
          :label="$t('business_account.onboarding_form.birth_zip')"
        />
      </div>
    </template>

    <div class="flex flex-1 flex-col gap-y-4 md:flex-row md:gap-x-4 md:gap-y-0">
      <BaseSelect
        v-model="v$.form.payment_volume_enum.$model"
        class="w-full md:w-1/2"
        :options="onboardingPaymentVolumeOptions"
        :loading="isLoading"
        :errors="errors.payment_volume_enum"
        :label="$t('business_account.onboarding_form.estimated_payments')"
      />
      <BaseSearchableSelect
        v-model="v$.form.business_enum.$model"
        class="w-full md:w-1/2"
        :loading="isLoading"
        :options="onboardingIndustryOptions"
        :errors="errors.business_enum"
        :label="$t('business_account.onboarding_form.industry')"
        :placeholder="$t('business_account.onboarding_form.industry')"
      />
    </div>

    <div class="flex flex-1 flex-col gap-y-4 md:flex-row md:gap-x-4 md:gap-y-0">
      <BaseInput
        v-model="v$.form.business_description.$model"
        class="w-full"
        :errors="errors.business_description"
        :label="$t('business_account.onboarding_form.business_description')"
      />
    </div>

    <LoadingSpinner
      v-if="showBusinessAccountLoading"
      class="self-center"
      :size="16"
    />
    <BusinessAccountOnboardingFormAdditional
      v-else
      :company
      :user
      :country-options
      :show-entrepreneur-information="isOyService"
      class="flex flex-1 flex-col text-white"
      @hide-dialog="hideBusinessAccountInformationDialog"
    />

    <div class="flex flex-1 flex-col">
      <CheckField
        v-model="v$.swan_terms.$model"
        class="text-white"
        :disabled="!isLoading && termsNotFound"
      >
        <I18nT
          scope="global"
          keypath="business_account.onboarding_form.swan_terms"
          for="swan_terms"
        >
          <BaseLink
            :href="onboardingData?.terms_of_service"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('business_account.onboarding_form.tos') }}
          </BaseLink>

          <BaseLink
            :href="onboardingData?.partnership_terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('business_account.onboarding_form.partnership_terms') }}
          </BaseLink>
        </I18nT>
        <LoadingSpinner v-if="isLoading" class="ml-2" :size="16" />
        <p v-if="!isLoading && termsNotFound" class="text-danger">
          {{ $t('business_account.onboarding_form.terms_error') }}
        </p>
      </CheckField>
      <BaseErrors :errors="errors.swan_terms" />
      <CheckField v-model="v$.terms.$model" class="mt-2 text-white">
        <I18nT
          scope="global"
          keypath="business_account.onboarding_form.terms"
          for="terms"
        >
          <BaseLink
            :href="$t('business_account.onboarding_form.tos_link')"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('business_account.onboarding_form.tos') }}
          </BaseLink>
        </I18nT>
      </CheckField>
      <BaseErrors :errors="errors.terms" />
    </div>
    <div class="flex flex-1 flex-col">
      <div class="mb-2 block text-center">
        <BaseButton
          filled
          class="inline-block rounded-full"
          :disabled="!canApplyForBusinessAccount || errorFetchingOnboardingData"
          :loading="isSubmitting || isLoading"
          @click.stop="validateFormAndOpenSwanOnboarding"
        >
          {{
            $t(
              'business_account.dialog_advertisement.columns_action_button_onboarding'
            )
          }}
        </BaseButton>
      </div>
      <p class="block text-center text-xs text-white">
        {{ $t('business_account.onboarding_form.passport_info') }}
      </p>
    </div>
    <BaseError
      v-if="hasOnboardingError"
      class="text-center"
      :error="$t('business_account.onboarding_form.error')"
    />
  </div>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

.onboarding-form :deep(.text-input) {
  margin-bottom: 0.25rem !important;
}
</style>
